/**
 * Agent-loss guard — what to do when the AI interviewer leaves mid-interview.
 *
 * The interview page used to treat the agent's departure as end-of-interview:
 * mark the session complete and navigate to the completion screen. That made a
 * production deploy unrecoverable. The agent worker is killed, the candidate is
 * navigated away within a second, and every recovery mechanism downstream needs
 * the candidate to still be in the room:
 *
 *   - LiveKit re-dispatches a fresh agent ~15s after it notices one vanished
 *   - the server re-dispatches when asked (POST /session/:id/ensure-agent)
 *   - the agent's session-resume prompt replays the persisted transcript so the
 *     replacement picks up mid-question instead of starting over
 *
 * Departure is a weak signal for "interview over". The strong signal is the
 * explicit completion the agent sends when its endInterview tool fires. So this
 * guard holds the room open, asks for a replacement, and only closes the
 * interview out if nobody arrives inside the grace window.
 *
 * Lives outside the component so the timing rules are directly testable —
 * the page itself needs LiveKit, router and media-device mocks to render.
 */

/**
 * How long to wait for a replacement interviewer before giving up.
 *
 * Sized against the recovery path rather than picked round: LiveKit takes ~15s
 * to notice the agent is gone, the server's verification loop retries every
 * 15s, and a freshly deployed worker must prewarm its VAD model before it can
 * accept the job. Matches the initial-join wait so the two behave alike.
 */
export const DEFAULT_AGENT_LOSS_GRACE_MS = 90000;

/**
 * @param {object} opts
 * @param {number}   [opts.graceMs]
 * @param {() => void} [opts.onLost]          Enter the "reconnecting" state.
 * @param {() => void} [opts.onRequestAgent]  Ask the server for a replacement.
 * @param {(elapsedMs: number) => void} [opts.onRecovered]
 * @param {() => void} [opts.onGiveUp]        Close the interview out.
 * @param {() => boolean} [opts.isCompleted]  True once the interview really ended.
 */
export function createAgentLossGuard({
  graceMs = DEFAULT_AGENT_LOSS_GRACE_MS,
  onLost = () => {},
  onRequestAgent = () => {},
  onRecovered = () => {},
  onGiveUp = () => {},
  isCompleted = () => false,
  now = () => Date.now(),
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  let timer = null;
  let lostAt = null;

  /**
   * The agent left the room while the interview was still running.
   * Idempotent — LiveKit can emit participantDisconnected more than once, and a
   * second call must not restart the grace window or fire a second request.
   */
  function agentLost() {
    if (isCompleted()) return false;
    if (timer !== null) return false;

    lostAt = now();

    // Arm the fallback BEFORE running any callback. If onRequestAgent throws
    // — a fetch rejecting synchronously, a bad base URL — the candidate must
    // still be closed out at the end of the window rather than left staring at
    // a reconnecting spinner forever.
    timer = setTimeoutFn(() => {
      timer = null;
      lostAt = null;
      // Re-check: the interview may have ended properly while we waited.
      if (isCompleted()) return;
      onGiveUp();
    }, graceMs);

    // A throwing UI callback must not prevent the request, and vice versa.
    try {
      onLost();
    } catch (err) {
      // Swallowed deliberately: the fallback above is what protects the user.
    }
    try {
      onRequestAgent();
    } catch (err) {
      // Best-effort nudge — LiveKit re-dispatches on its own too.
    }

    return true;
  }

  /**
   * A replacement interviewer joined. Cancels the give-up fallback.
   * Safe to call when no grace window is running (the normal join path).
   */
  function agentJoined() {
    if (timer === null) return false;
    clearTimeoutFn(timer);
    timer = null;
    const elapsed = lostAt === null ? 0 : now() - lostAt;
    lostAt = null;
    onRecovered(elapsed);
    return true;
  }

  /** Drop the pending fallback without firing it — unmount, teardown. */
  function cancel() {
    if (timer !== null) {
      clearTimeoutFn(timer);
      timer = null;
    }
    lostAt = null;
  }

  return {
    agentLost,
    agentJoined,
    cancel,
    isPending: () => timer !== null,
    lostAtMs: () => lostAt,
  };
}
